import { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import useSessionState from "../hooks/useSessionState";
import apiClient from "../helpers/apiClient";

const CarContext = createContext();

const DEFAULT_PAGE_SIZE = 10;

const fetchCarLocation = async () => {
    //     try {
    //         const response = await axios.put(
    //             "https://softcar-chrentacar-live-64df6dffd12e.herokuapp.com/location",
    //             { serialNumber: serialNumber.toString() },
    //             {
    //                 headers: {
    //                     "Content-Type": "application/json",
    //                     "Access-Control-Allow-Origin": "*",
    //                     "application-type": "dronemobile",
    //                 },
    //             },
    //         );
    //         return response.data;
    //     } catch (error) {
    //         console.log(
    //             `LOCATION ERROR for car with serialNumber: ${serialNumber}, error: `,
    //             error?.response?.data || error,
    //         );
    //         return { error: true };
    //     }
};

export const CarProvider = ({ children }) => {
    const [cars, setCars] = useState([]);
    const [carLocations, setCarLocations] = useState([]);
    const [searchParams, setSearchParams] = useState({});
    const [pageSize, setPageSize] = useSessionState("cars:pageSize", DEFAULT_PAGE_SIZE);
    const [loading, setLoading] = useState(false);
    const [mapLoading, setMapLoading] = useState(true);
    const [initialLoad, setInitialLoad] = useState(true);
    const [activePageNumber, setActivePageNumber] = useSessionState("cars:page", 1);
    const [totalPageCount, setTotalPageCount] = useState(1);
    const [totalItemCount, setTotalItemCount] = useState(0);
    const [activeFilter, setActiveFilter] = useState(1);
    const lastSearchParamsKeyRef = useRef("{}");

    const fetchCars = useCallback(
        async (resetPage = false) => {
            try {
                setLoading(true);

                const pageToUse = resetPage ? 1 : activePageNumber;
                if (resetPage) {
                    setActivePageNumber(1);
                }

                const queryParams = new URLSearchParams({
                    limit: pageSize.toString(),
                    page: pageToUse.toString(),
                    ...searchParams,
                });

                // Apply filter status at API level
                if (activeFilter === 2) {
                    queryParams.append("status", "Locked");
                } else if (activeFilter === 3) {
                    queryParams.append("status", "Unlocked");
                }

                const response = await apiClient.get(`/api/car/list?${queryParams}`);

                if (response?.data?.success) {
                    const { meta, result } = response.data.data;

                    const totalItems = meta?.total || 0;
                    const totalPages = Math.ceil(totalItems / pageSize);

                    setTotalItemCount(totalItems);
                    setTotalPageCount(totalPages);
                    setCars(result || []);
                    setInitialLoad(false);
                    setCarLocations([]);

                    // Fetch locations for each car
                    const locationPromises = result.map(async (car) => {
                        const location = await fetchCarLocation(car.serialNumber);
                        return !location?.error ? { ...location, ...car } : null;
                    });

                    const locations = (await Promise.all(locationPromises)).filter(Boolean);
                    setCarLocations(locations);
                    setMapLoading(false);
                }
            } catch (_error) {
                // console.log("Error fetching cars:", _error);
                setMapLoading(false);
                setInitialLoad(false);
            } finally {
                setLoading(false);
            }
        },
        [activeFilter, activePageNumber, pageSize, searchParams, setActivePageNumber],
    );

    useEffect(() => {
        if (!initialLoad) {
            fetchCars();
        }
    }, [searchParams, activePageNumber, activeFilter, pageSize, fetchCars, initialLoad]);

    const deleteCar = async (carId) => {
        try {
            const response = await apiClient.delete(`/api/car/${carId}`);
            if (response?.data?.success) {
                // Refetch cars after deletion
                await fetchCars();
                return { success: true };
            }
            return { success: false };
        } catch (error) {
            // console.error("Error deleting car:", error);
            return { success: false, error };
        }
    };

    const updateSearchParams = useCallback((params) => {
        const nextKey = JSON.stringify(params);
        if (nextKey === lastSearchParamsKeyRef.current) {
            return;
        }
        lastSearchParamsKeyRef.current = nextKey;
        setSearchParams(params);
        setActivePageNumber(1);
    }, [setActivePageNumber]);

    const updateActiveFilter = (filter) => {
        setActiveFilter(filter);
        setActivePageNumber(1); // Reset page when filter changes
    };

    const updateActivePage = useCallback((page) => {
        setActivePageNumber(page);
    }, [setActivePageNumber]);

    const resetCarState = () => {
        setCars([]);
        setCarLocations([]);
        setSearchParams({});
        setPageSize(DEFAULT_PAGE_SIZE);
        setActivePageNumber(1);
        setActiveFilter(1);
        setInitialLoad(true);
    };

    const value = {
        // State
        cars,
        carLocations,
        searchParams,
        pageSize,
        loading,
        mapLoading,
        initialLoad,
        activePageNumber,
        totalPageCount,
        totalItemCount,
        activeFilter,
        // Actions
        fetchCars,
        deleteCar,
        updateSearchParams,
        updateActiveFilter,
        updateActivePage,
        setPageSize,
        resetCarState,
        setCars,
    };

    return <CarContext.Provider value={value}>{children}</CarContext.Provider>;
};

CarProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

export const useCarContext = () => {
    const context = useContext(CarContext);
    if (!context) {
        throw new Error("useCarContext must be used within a CarProvider");
    }
    return context;
};

export default CarContext;
